import { useCallback, useMemo } from "react";
import { useLocalStorage } from "./useLocalStorage";
import { STORAGE_KEYS } from "../constants/storageKeys";

/**
 * Custom hook to manage viewed loads state.
 * Persists viewed load IDs to localStorage for cross-session persistence.
 *
 * @returns {Object} Hook API
 * @returns {Set<string>} viewedLoadIds - Set of viewed load IDs for O(1) lookups
 * @returns {function} isLoadViewed - Check if a specific load has been viewed
 * @returns {function} markLoadAsViewed - Mark a single load as viewed
 * @returns {function} markMultipleLoadsAsViewed - Mark multiple loads as viewed at once
 * @returns {function} clearViewedLoads - Clear all viewed loads
 */
export const useViewedLoads = () => {
  const [viewedLoadIdsArray, setViewedLoadIdsArray] = useLocalStorage(
    STORAGE_KEYS.VIEWED_LOAD_IDS,
    []
  );

  // Convert array to Set for O(1) lookups - memoized to prevent recreation on each render
  const viewedLoadIds = useMemo(
    () => new Set(Array.isArray(viewedLoadIdsArray) ? viewedLoadIdsArray : []),
    [viewedLoadIdsArray]
  );

  // Check if a load has been viewed
  const isLoadViewed = useCallback(
    (loadId) => {
      if (!loadId) return false;
      return viewedLoadIds.has(String(loadId));
    },
    [viewedLoadIds]
  );

  // Mark a single load as viewed
  const markLoadAsViewed = useCallback(
    (loadId) => {
      if (!loadId) return;

      const loadIdStr = String(loadId);
      if (viewedLoadIds.has(loadIdStr)) return; // Already viewed, no need to update

      setViewedLoadIdsArray((prev) => {
        const currentArray = Array.isArray(prev) ? prev : [];
        // Double-check to avoid duplicates
        if (currentArray.includes(loadIdStr)) return prev;
        return [...currentArray, loadIdStr];
      });
    },
    [viewedLoadIds, setViewedLoadIdsArray]
  );

  // Mark multiple loads as viewed at once
  const markMultipleLoadsAsViewed = useCallback(
    (loadIds) => {
      if (!Array.isArray(loadIds) || loadIds.length === 0) return;

      setViewedLoadIdsArray((prev) => {
        const currentSet = new Set(Array.isArray(prev) ? prev : []);
        let hasNewIds = false;

        loadIds.forEach((id) => {
          if (id) {
            const idStr = String(id);
            if (!currentSet.has(idStr)) {
              currentSet.add(idStr);
              hasNewIds = true;
            }
          }
        });

        // Only update if we actually added new IDs
        if (!hasNewIds) return prev;
        return [...currentSet];
      });
    },
    [setViewedLoadIdsArray]
  );

  // Clear all viewed loads
  const clearViewedLoads = useCallback(() => {
    setViewedLoadIdsArray([]);
  }, [setViewedLoadIdsArray]);

  return {
    viewedLoadIds,
    isLoadViewed,
    markLoadAsViewed,
    markMultipleLoadsAsViewed,
    clearViewedLoads,
  };
};
